import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, BookOpenText, Sparkles, Microscope } from "lucide-react";
import TrendingPairs from "@/components/TrendingPairs";

const STATS = [
  { label: "Mutation entries", value: "27", note: "Worldwide catalog" },
  { label: "Loci modelled", value: "13", note: "Full Punnett" },
  { label: "Inheritance modes", value: "4", note: "Auto/dom/rec/Z-link" },
  { label: "Outcome accuracy", value: "100%", note: "Mendelian" },
];

const FEATURES = [
  {
    icon: FlaskConical,
    title: "Breeding Calculator",
    description:
      "Pair any cock and hen by mutation. Get exact probabilities of every visual and split combination, including sex-linked inheritance.",
    to: "/calculator",
    cta: "Open calculator",
    testId: "feature-calculator",
  },
  {
    icon: BookOpenText,
    title: "Mutation Index",
    description:
      "Every documented budgie mutation — from wild-type Light Green to Anthracite, Saddleback and the legendary Rainbow combination.",
    to: "/encyclopedia",
    cta: "Browse the index",
    testId: "feature-encyclopedia",
  },
  {
    icon: Sparkles,
    title: "AI Chick Notes",
    description:
      "Each predicted offspring earns its own AI-written lab-notebook description — plumage, eye colour, show potential.",
    to: "/calculator",
    cta: "See in action",
    testId: "feature-ai",
  },
  {
    icon: Microscope,
    title: "Genetics 101",
    description:
      "Plain-language primer on dominance, the dark-factor series, ZW sex-linkage and split birds. For new breeders.",
    to: "/genetics-101",
    cta: "Read primer",
    testId: "feature-primer",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="border-b border-border lab-grid-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6" data-testid="hero-overline">
              File 001 · Melopsittacus undulatus · Colour & Pattern
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
              A lab notebook for{" "}
              <span className="bg-primary text-primary-foreground px-2">
                budgerigar
              </span>{" "}
              genetics.
            </h1>
            <p className="mt-6 text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Predict offspring colour and mutation outcomes with full
              Punnett-square accuracy. Browse a worldwide catalogue of budgie
              mutations. Free, open, no account required.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/calculator"
                data-testid="hero-cta-calculator"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium"
              >
                Run a breeding pair <ArrowRight size={16} />
              </Link>
              <Link
                to="/encyclopedia"
                data-testid="hero-cta-encyclopedia"
                className="inline-flex items-center gap-2 border border-border px-5 py-3 rounded-sm hover:bg-secondary transition-colors font-medium"
              >
                Open mutation index
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="overline">Sample Prediction</div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  P(♂ × ♀)
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: "Sky Blue Spangle (♂)", pct: "25.0%", color: "#A8D4E6" },
                  { name: "Sky Blue (♀)", pct: "25.0%", color: "#7FBDD9" },
                  { name: "Cobalt /opaline (♂)", pct: "12.5%", color: "#2C5DA0" },
                  { name: "Albino (♀)", pct: "6.25%", color: "#F5F5F5" },
                ].map((row) => (
                  <div
                    key={row.name}
                    className="border border-border p-3 flex items-center gap-3"
                    data-testid={`hero-sample-${row.name}`}
                  >
                    <div
                      className="w-8 h-8 border border-border"
                      style={{ background: row.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">
                        {row.name}
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {row.pct}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 border-t border-border pt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Sample · Bb/Dd ♂ × bb/dd ♀
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS strip */}
      <section className="border-b border-border" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="border-l border-border first:border-l-0 px-6 py-8"
              data-testid={`stat-${s.label}`}
            >
              <div className="overline">{s.label}</div>
              <div className="mt-2 text-4xl font-black">{s.value}</div>
              <div className="mt-1 text-xs text-muted-foreground">{s.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TRENDING PAIRS */}
      <TrendingPairs />

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURES.map(({ icon: Icon, title, description, to, cta, testId }) => (
            <Link
              to={to}
              key={title}
              data-testid={testId}
              className="border border-border bg-card p-6 lg:p-8 group hover:border-primary/40 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-border grid place-items-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Icon size={18} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold tracking-tight">{title}</h3>
                  <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                    {description}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary">
                    {cta} <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* QUOTE / Note */}
      <section className="border-t border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="overline mb-3">Lab note · Reading the genotype</div>
          <p className="text-2xl lg:text-3xl font-bold tracking-tight max-w-4xl leading-snug">
            <span className="genotype-chip">bb · Dd · ii</span> is a cock
            carrying two blue alleles, heterozygous for dark factor, and
            homozygous for ino — phenotype:{" "}
            <span className="underline decoration-primary decoration-2 underline-offset-4">
              Albino Cobalt cock
            </span>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
